import { useQuery } from '@tanstack/react-query';
import type {
  CompetitorAnalysis,
  CompetitorAnalysisCluster,
  CompetitorAnalysisGap,
  CompetitorAnalysisPage,
  CursorPage,
} from '@seo/types';
import { apiGet } from '@/lib/api';
import { isTerminal } from '@/lib/queryClient';
import type { CompetitorAnalysisInsights } from './insights-types';

export function useCompetitorAnalysis(id: string | undefined) {
  return useQuery({
    queryKey: ['competitor-analysis', id],
    queryFn: () => apiGet<CompetitorAnalysis>(`/v1/competitor-analyses/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (isTerminal(q.state.data?.status) ? false : 4_000),
  });
}

export function useCompetitorAnalysisInsights(
  id: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['competitor-analysis-insights', id],
    queryFn: () =>
      apiGet<CompetitorAnalysisInsights>(`/v1/competitor-analyses/${id}/insights`),
    enabled: !!id && enabled,
  });
}

export function useCompetitorAnalysisPages(id: string | undefined) {
  return useQuery({
    queryKey: ['competitor-analysis-pages', id],
    queryFn: () =>
      apiGet<CursorPage<CompetitorAnalysisPage>>(
        `/v1/competitor-analyses/${id}/pages`,
      ),
    enabled: !!id,
  });
}

export function useCompetitorAnalysisClusters(id: string | undefined) {
  return useQuery({
    queryKey: ['competitor-analysis-clusters', id],
    queryFn: () =>
      apiGet<CursorPage<CompetitorAnalysisCluster>>(
        `/v1/competitor-analyses/${id}/clusters`,
      ),
    enabled: !!id,
  });
}

export function useCompetitorAnalysisGaps(
  id: string | undefined,
  opts?: { all?: boolean },
) {
  const all = opts?.all === true;
  return useQuery({
    queryKey: ['competitor-analysis-gaps', id, all ? 'all' : 'top'],
    queryFn: () =>
      apiGet<CursorPage<CompetitorAnalysisGap> & { total?: number }>(
        `/v1/competitor-analyses/${id}/gaps${all ? '?all=1' : ''}`,
      ),
    enabled: !!id,
  });
}
